import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Image, Phone, Mail, Heart, Send, Share2, Users, Calendar, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';

const PropertyAnalyticsDetailPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [propertyData, setPropertyData] = useState(null);

    useEffect(() => {
        // For now, we'll show a placeholder
        // In a full implementation, you could fetch detailed analytics for this specific property
        setLoading(false);

        // Placeholder data - in production, fetch from API
        setPropertyData({
            property_id: propertyId,
            total_interactions: 0,
            property_views: 0,
            gallery_views: 0,
            calls_clicked: 0,
            emails_clicked: 0,
            properties_saved: 0,
            enquiries_sent: 0,
            shares_clicked: 0,
            unique_buyers: 0,
        });
    }, [propertyId]);

    const handleBack = () => {
        navigate('/analytics');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="text-gray-600">Loading property analytics...</div>
                    </div>
                </div>
            </div>
        );
    }

    const interactions = [
        { icon: Eye, label: 'Property Views', count: propertyData?.property_views || 0, color: 'blue' },
        { icon: Image, label: 'Gallery Views', count: propertyData?.gallery_views || 0, color: 'purple' },
        { icon: Phone, label: 'Phone Clicks', count: propertyData?.calls_clicked || 0, color: 'green' },
        { icon: Mail, label: 'Email Clicks', count: propertyData?.emails_clicked || 0, color: 'orange' },
        { icon: Heart, label: 'Times Saved', count: propertyData?.properties_saved || 0, color: 'red' },
        { icon: Send, label: 'Enquiries Sent', count: propertyData?.enquiries_sent || 0, color: 'indigo' },
        { icon: Share2, label: 'Shares', count: propertyData?.shares_clicked || 0, color: 'teal' },
        { icon: Users, label: 'Unique Buyers', count: propertyData?.unique_buyers || 0, color: 'pink' },
    ];

    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600 border-blue-200',
        purple: 'bg-purple-50 text-purple-600 border-purple-200',
        green: 'bg-green-50 text-green-600 border-green-200',
        orange: 'bg-orange-50 text-orange-600 border-orange-200',
        red: 'bg-red-50 text-red-600 border-red-200',
        indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
        teal: 'bg-teal-50 text-teal-600 border-teal-200',
        pink: 'bg-pink-50 text-pink-600 border-pink-200',
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Back to Analytics</span>
                </button>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                <TrendingUp className="w-8 h-8 text-blue-600" />
                                Property #{propertyId} Analytics
                            </h1>
                            <p className="text-gray-600 mt-2">
                                Detailed interaction analytics for this property
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-4xl font-bold text-blue-600">
                                {propertyData?.total_interactions || 0}
                            </div>
                            <div className="text-sm text-gray-600">Total Interactions</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Interaction Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {interactions.map((item, index) => {
                    const Icon = item.icon;
                    return (
                        <div
                            key={index}
                            className={`bg-white rounded-lg shadow p-6 border-l-4 ${colorClasses[item.color]}`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">{item.label}</p>
                                    <p className="text-3xl font-bold text-gray-900 mt-2">{item.count}</p>
                                </div>
                                <div className={`p-3 rounded-lg ${colorClasses[item.color]}`}>
                                    <Icon className="w-6 h-6" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Additional Information */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Property Information</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                        <strong>Note:</strong> This is a detailed analytics view for Property #{propertyId}.
                        In a full implementation, this page would show:
                    </p>
                    <ul className="list-disc list-inside mt-2 text-sm text-blue-700 space-y-1">
                        <li>Property details (address, price, images)</li>
                        <li>Timeline of all interactions</li>
                        <li>List of buyers who interacted with this property</li>
                        <li>Interaction trends over time (charts)</li>
                        <li>Comparison with similar properties</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PropertyAnalyticsDetailPage;
